import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { FightPlayer, FightSetup, SpellAssignment } from "web/data/models"
import { uniqueIdFromSpellAssignment, getPlayerSpells } from 'web/engine/spell-engine'
import { assertDefined } from 'web/utils/utils'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { getBuildByPatchInfo } from 'web/data/core-data'

export type EditSetupState = {
    fightId: string | undefined
    setup: FightSetup | undefined
    comparisonActive: boolean
}

const initialState: EditSetupState = {
    setup: undefined,
    fightId: undefined,
    comparisonActive: false
}

const slice = createSlice({
    name: "edit-setup",
    initialState,
    reducers: {

        /**
         * Special side-channel for loading slice data directly from a
         * setup-recovery. Do not attempt to use for anything else.
         *
         * This is effectively a `preloadedState` call.
         */
        loadRecovery: (_, action: PayloadAction<{ recoveryJson: string }>) => {
            const jsonState = JSON.parse(action.payload.recoveryJson) as EditSetupState

            return {
                ...jsonState
            }
        },

        beginEdit: (state, action: PayloadAction<{ fightId: string; setup: FightSetup }>) => {
            state.setup = action.payload.setup
            state.fightId = action.payload.fightId
        },

        endEdit: () => {
            return initialState
        },

        toggleComparison: (state, action: PayloadAction<{ comparisonActive: boolean }>) => {
            return {
                ...state,
                comparisonActive: action.payload.comparisonActive
            }
        },

        addSpellAssignment: (state, action: PayloadAction<{ spellAssignment: SpellAssignment }>) => {
            const { spellAssignment } = action.payload

            assertDefined(state.setup)
            state.setup.spellAssignments.push(spellAssignment)
        },

        removeSpellAssignment: (state, action: PayloadAction<{ saUniqueId: string }>) => {
            const { saUniqueId } = action.payload

            assertDefined(state.setup)

            state.setup.spellAssignments = state.setup.spellAssignments
                .filter(sa => uniqueIdFromSpellAssignment(sa) !== saUniqueId)
        },

        addPlayer: (state, action: PayloadAction<{ player: FightPlayer }>) => {
            assertDefined(state.setup)
            state.setup.fightPlayers.push(action.payload.player)
        },

        updatePlayer: (state, action: PayloadAction<{ player: FightPlayer }>) => {
            const { player } = action.payload

            assertDefined(state.setup)

            state.setup.fightPlayers = [
                ...state.setup.fightPlayers.filter(p => p.id !== player.id),
                player
            ]

            // TODO: This block is bordering on needing a test
            state.setup.spellAssignments =
                state.setup.spellAssignments
                    .filter(sa => {
                        // We only care for the player we just edited
                        if (sa.playerId !== action.payload.player.id) {
                            return true
                        }

                        const availableSpells = getPlayerSpells(player)

                        const spell = getBuildByPatchInfo(player.patchInfo)
                            .Spell(sa.spellId)

                        return availableSpells.find(s => s.id === spell.id) != null

                    })
        },

        removePlayer: (state, action: PayloadAction<{ playerId: string }>) => {
            assertDefined(state.setup)

            state.setup.spellAssignments = state.setup.spellAssignments.filter(
                sa => sa.playerId !== action.payload.playerId
            )

            state.setup.fightPlayers = state.setup.fightPlayers.filter(
                p => p.id !== action.payload.playerId
            )
        },

        setName: (state, action: PayloadAction<{ name: string }>) => {
            assertDefined(state.setup)
            state.setup.name = action.payload.name
        }
    }
})

export const EditSetupActions = slice.actions
export const EditSetupReducer = slice.reducer

export const useEditSetupState = () =>
    useSelector<RootState, EditSetupState>(s => s.editSetup)
